import { useEffect, useRef } from 'react';

import { type IYmapsKeys, type IYmapsPanoramaPlayer, loadYmapsApi, type TYmapsCoordinates } from 'shared/utils/ymaps';

import style from './style.css';

const PANORAMA_MODULES = ['panorama.isSupported', 'panorama.locate', 'panorama.Player'];

export interface IObjectOnPanoramaProps {
  coordinates: TYmapsCoordinates;
  keys: IYmapsKeys;
  /** false — панорамы рядом нет, браузер их не поддерживает или API не загрузился */
  onLoad(isLoaded: boolean): void;
  onError(error: unknown): void;
}

/** Ближайшая к зданию панорама — как `object_on_panorama.tsx` монолита */
export const ObjectOnPanorama = ({ coordinates, keys, onLoad, onError }: IObjectOnPanoramaProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [lat, lng] = coordinates;
  const { apiKey, suggestApiKey } = keys;

  useEffect(() => {
    let player: IYmapsPanoramaPlayer | null = null;
    let isCancelled = false;

    const locate = async () => {
      const ymaps = await loadYmapsApi({ apiKey, suggestApiKey }, PANORAMA_MODULES);

      // Монолит в этом случае оставляет вкладку пустой — показываем «Панорамы отсутствуют»
      if (!ymaps.panorama.isSupported()) {
        return false;
      }

      const [panorama] = await ymaps.panorama.locate([lat, lng]);

      if (isCancelled || !ref.current || !panorama) {
        return false;
      }

      player = new ymaps.panorama.Player(ref.current, panorama);

      return true;
    };

    locate()
      .catch(error => {
        onError(error);

        return false;
      })
      .then(isLoaded => {
        if (!isCancelled) {
          onLoad(isLoaded);
        }
      });

    return () => {
      isCancelled = true;
      player?.destroy();
    };
  }, [lat, lng, apiKey, suggestApiKey, onLoad, onError]);

  return <div ref={ref} className={style['panorama']} data-name="ObjectOnPanorama" />;
};
